import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, 
  CreditCard, 
  Truck, 
  Package, 
  Settings, 
  Store, 
  ArrowRight,
  CheckCircle2,
  Globe,
  Users,
  Search,
  BarChart,
  Smartphone,
  Shield,
  Zap,
  DollarSign,
  Boxes,
  Share2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Store,
    title: 'WooCommerce og Shopify',
    description: 'Öflugar netverslanir með WooCommerce eða Shopify.'
  },
  {
    icon: CreditCard,
    title: 'Greiðslugáttir',
    description: 'Tengingar við allar helstu greiðslugáttir.'
  },
  {
    icon: Truck,
    title: 'Sendingarlausnir',
    description: 'Sjálfvirkar sendingarlausnir og eftirfylgni.'
  },
  {
    icon: Package,
    title: 'Birgðastýring',
    description: 'Öflug birgðastýring og vörustjórnun.'
  },
  {
    icon: Settings,
    title: 'Sérsniðin virkni',
    description: 'Sérsniðin virkni fyrir þitt fyrirtæki.'
  },
  {
    icon: BarChart,
    title: 'Sölugreining',
    description: 'Ítarleg greining á sölu og hegðun viðskiptavina.'
  }
];

const wooCommerceFeatures = [
  {
    icon: Globe,
    title: 'Open Source',
    description: 'Opinn og frjáls hugbúnaður með fullu frelsi til sérsniðinna lausna.'
  },
  {
    icon: DollarSign,
    title: 'Engin mánaðargjöld',
    description: 'Aðeins greiðslur fyrir hýsingu og viðbætur sem þú velur.'
  },
  {
    icon: Settings,
    title: 'Fullkomin stjórn',
    description: 'Full stjórn á kóða, gögnum og virkni.'
  },
  {
    icon: Boxes,
    title: 'Fjöldi viðbóta',
    description: 'Aðgangur að þúsundum ókeypis og keyptra viðbóta.'
  },
  {
    icon: Share2,
    title: 'Samþætting',
    description: 'Auðveld samþætting við önnur kerfi og þjónustur.'
  },
  {
    icon: Shield,
    title: 'Öryggi',
    description: 'Reglulegar öryggisuppfærslur og sterk samfélagsvernd.'
  }
];

const shopifyFeatures = [
  {
    icon: Zap,
    title: 'Auðveld uppsetning',
    description: 'Hraðvirk og einföld uppsetning með tilbúnum sniðmátum.'
  },
  {
    icon: Shield,
    title: 'Innbyggt öryggi',
    description: 'Örugg hýsing og reglulegar uppfærslur innifaldar.'
  },
  {
    icon: Users,
    title: 'Þjónusta',
    description: '24/7 þjónusta og stuðningur frá Shopify.'
  },
  {
    icon: Smartphone,
    title: 'Innbyggð app',
    description: 'Tilbúin app fyrir iOS og Android.'
  },
  {
    icon: Search,
    title: 'SEO vænt',
    description: 'Innbyggð leitarvélabestun og markaðstól.'
  },
  {
    icon: CreditCard,
    title: 'Greiðslugáttir',
    description: 'Innbyggðar greiðslulausnir og lág þóknun.'
  }
];

const comparison = [
  {
    feature: 'Stofnkostnaður',
    woocommerce: 'Lágur',
    shopify: 'Enginn'
  },
  {
    feature: 'Mánaðargjöld',
    woocommerce: 'Engin',
    shopify: '$29-299/mán'
  },
  {
    feature: 'Þóknun á sölu',
    woocommerce: 'Engin',
    shopify: '0.5-2%'
  },
  {
    feature: 'Sérsniðin virkni',
    woocommerce: 'Ótakmörkuð',
    shopify: 'Takmörkuð'
  },
  {
    feature: 'Hýsing',
    woocommerce: 'Þarf að kaupa',
    shopify: 'Innifalin'
  },
  {
    feature: 'Uppsetningartími',
    woocommerce: 'Lengri',
    shopify: 'Stuttur'
  },
  {
    feature: 'Viðhald',
    woocommerce: 'Meira',
    shopify: 'Minna'
  },
  {
    feature: 'Sveigjanleiki',
    woocommerce: 'Mikill',
    shopify: 'Takmarkaður'
  }
];

const Ecommerce = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Netverslun sem skilar árangri
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Við setjum upp öfluga netverslun með WooCommerce eða Shopify sem 
              hentar þínum þörfum. Fagleg uppsetning, öruggar greiðslulausnir og 
              framúrskarandi þjónusta.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá tilboð í netverslun
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Allt sem þú þarft í netverslun
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á heildarlausn fyrir netverslanir
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* WooCommerce Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              WooCommerce netverslanir
            </h2>
            <p className="text-xl text-gray-600">
              Öflug og sveigjanleg lausn byggð á WordPress
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {wooCommerceFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Shopify Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Shopify netverslanir
            </h2>
            <p className="text-xl text-gray-600">
              Fullkomin lausn fyrir þá sem vilja einfalda og örugga netverslun
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {shopifyFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Comparison Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              WooCommerce vs Shopify
            </h2>
            <p className="text-xl text-gray-600">
              Samanburður á helstu þáttum
            </p>
          </motion.div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Eiginleiki</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">WooCommerce</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Shopify</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {comparison.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.feature}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{item.woocommerce}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{item.shopify}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að opna netverslun?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að velja réttu lausnina og koma þinni 
              netverslun í loftið
            </motion.p>
            <motion.div>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Fá tilboð í netverslun
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Ecommerce;